import React, { useState, useEffect } from 'react';
import { Modal, Radio, Checkbox, Button, message, Divider } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { d201Api } from '../../../services/d201Api';

// ── Types ──────────────────────────────────────────────────────

/** PO version: PEPWING (P1) or KEYCHAIN (P3) */
type POVersion = 'P1' | 'P3';

/** Props for PrintOptionsModal */
interface PrintOptionsModalProps {
  visible: boolean;
  poNo: string;
  onClose: () => void;
}

// ── Component ──────────────────────────────────────────────────

const PrintOptionsModal: React.FC<PrintOptionsModalProps> = ({
  visible,
  poNo,
  onClose,
}) => {
  // Report version (PEPWING / KEYCHAIN) — applies to the main PO report
  const [version, setVersion] = useState<POVersion>('P1');
  // Checkboxes
  const [printPO, setPrintPO] = useState(true);       // 採購單報表 (P1 or P3)
  const [printInspection, setPrintInspection] = useState(false); // 檢驗規範 (P12)
  const [printAlt, setPrintAlt] = useState(false);    // 另一格式 (P4)

  // Reset to defaults every time the modal opens
  useEffect(() => {
    if (visible) {
      setVersion('P1');
      setPrintPO(true);
      setPrintInspection(false);
      setPrintAlt(false);
    }
  }, [visible]);

  // At least one report must be selected
  const hasSelection = printPO || printInspection || printAlt;

  /** Build the comma-separated reports string and open PDF in a new tab */
  const handlePrint = () => {
    if (!hasSelection) {
      message.warning('請至少勾選一個列印報表');
      return;
    }

    const reports: string[] = [];
    if (printPO) reports.push(version);   // P1 or P3 depending on version choice
    if (printInspection) reports.push('P12');
    if (printAlt) reports.push('P4');

    const url = d201Api.getPrintUrl(poNo, { reports: reports.join(',') });
    window.open(url, '_blank');
    onClose();
  };

  return (
    <Modal
      title="列印選項"
      open={visible}
      onCancel={onClose}
      width={440}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          取消
        </Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          disabled={!hasSelection}
          onClick={handlePrint}
        >
          列印預覽
        </Button>,
      ]}
    >
      {/* PO number display */}
      <p style={{ marginBottom: 16 }}>
        採購單號：<strong>{poNo}</strong>
      </p>

      {/* ── 報表版本 ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 6, fontWeight: 500 }}>報表版本：</div>
        <Radio.Group
          value={version}
          onChange={(e) => setVersion(e.target.value as POVersion)}
        >
          <Radio value="P1">PEPWING版</Radio>
          <Radio value="P3">KEYCHAIN版</Radio>
        </Radio.Group>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── 列印報表勾選 ── */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ marginBottom: 6, fontWeight: 500 }}>列印報表：</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 8 }}>
          <Checkbox
            checked={printPO}
            onChange={(e) => setPrintPO(e.target.checked)}
          >
            採購單報表
          </Checkbox>
          <Checkbox
            checked={printInspection}
            onChange={(e) => setPrintInspection(e.target.checked)}
          >
            檢驗規範
          </Checkbox>
          <Checkbox
            checked={printAlt}
            onChange={(e) => setPrintAlt(e.target.checked)}
          >
            另一格式 (P4)
          </Checkbox>
        </div>
        {/* Validation hint */}
        {!hasSelection && (
          <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, paddingLeft: 8 }}>
            請至少勾選一個列印報表
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PrintOptionsModal;
